import React from 'react';
import type { InvoiceTheme } from '../../types/billing.types';
import SectionAccordion from './SectionAccordion';
import ColorPickerInput from './ColorPickerInput';

interface ThemeEditorFormProps {
  theme: Partial<InvoiceTheme>;
  onChange: (field: string, value: unknown) => void;
}

const FONT_OPTIONS = [
  'Arial, Helvetica, sans-serif',
  'Georgia, serif',
  'Times New Roman, serif',
  'Courier New, monospace',
  'Verdana, sans-serif',
  'Trebuchet MS, sans-serif',
  'Garamond, serif',
  'Palatino, serif',
];

const Toggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between py-1 cursor-pointer">
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-lex-accent' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </button>
  </label>
);

const NumberInput: React.FC<{ label: string; value: number; min?: number; max?: number; step?: number; suffix?: string; onChange: (v: number) => void }> = ({ label, value, min, max, step = 1, suffix, onChange }) => (
  <div className="flex items-center justify-between">
    <label className="text-sm text-gray-700 dark:text-gray-300">{label}</label>
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white text-right"
      />
      {suffix && <span className="text-xs text-gray-500 dark:text-gray-400 w-6">{suffix}</span>}
    </div>
  </div>
);

const TextInput: React.FC<{ label: string; value: string; placeholder?: string; onChange: (v: string) => void }> = ({ label, value, placeholder, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
    />
  </div>
);

const ThemeEditorForm: React.FC<ThemeEditorFormProps> = ({ theme, onChange }) => {
  const v = (field: string, fallback: unknown = '') => (theme as Record<string, unknown>)[field] ?? fallback;
  const vb = (field: string, fallback = true) => (v(field, fallback) as boolean);
  const vn = (field: string, fallback = 0) => Number(v(field, fallback));
  const vs = (field: string, fallback = '') => String(v(field, fallback));

  return (
    <div className="space-y-3 overflow-y-auto">
      {/* Section 1: Identity */}
      <SectionAccordion title="Identity" defaultOpen={true}>
        <TextInput label="Theme Name" value={vs('name')} placeholder="e.g. Professional Blue" onChange={(v) => onChange('name', v)} />
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
          <textarea
            rows={2}
            value={vs('description')}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="Brief description of this theme..."
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
          />
        </div>
        <Toggle label="Set as Default" checked={vb('is_default', false)} onChange={(v) => onChange('is_default', v)} />
      </SectionAccordion>

      {/* Section 2: Firm Branding */}
      <SectionAccordion title="Firm Branding" defaultOpen={true}>
        <TextInput label="Firm Name" value={vs('firm_name')} placeholder="Your Law Firm LLP" onChange={(v) => onChange('firm_name', v)} />
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Firm Address</label>
          <textarea
            rows={3}
            value={vs('firm_address')}
            onChange={(e) => onChange('firm_address', e.target.value)}
            placeholder="123 Legal Ave&#10;Suite 100&#10;New York, NY 10001"
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
          />
        </div>
        <TextInput label="Phone" value={vs('firm_phone')} placeholder="(212) 555-0100" onChange={(v) => onChange('firm_phone', v)} />
        <TextInput label="Email" value={vs('firm_email')} placeholder="billing@firm.com" onChange={(v) => onChange('firm_email', v)} />
        <TextInput label="Website" value={vs('firm_website')} placeholder="https://www.firm.com" onChange={(v) => onChange('firm_website', v)} />
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <TextInput label="Logo URL" value={vs('logo_url')} placeholder="https://..." onChange={(v) => onChange('logo_url', v)} />
          <div className="flex items-center gap-4 mt-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Alignment</label>
            {(['left', 'center', 'right'] as const).map((align) => (
              <label key={align} className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name="logo_alignment"
                  checked={vs('logo_alignment', 'left') === align}
                  onChange={() => onChange('logo_alignment', align)}
                  className="text-lex-accent"
                />
                <span className="text-gray-600 dark:text-gray-400 capitalize">{align}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-3 mt-2">
            <NumberInput label="Logo Width" value={vn('logo_width', 200)} min={50} max={600} suffix="px" onChange={(v) => onChange('logo_width', v)} />
            <NumberInput label="Height" value={vn('logo_height', 80)} min={20} max={200} suffix="px" onChange={(v) => onChange('logo_height', v)} />
          </div>
        </div>
      </SectionAccordion>

      {/* Section 3: Color Scheme */}
      <SectionAccordion title="Color Scheme">
        <ColorPickerInput label="Primary Color" value={vs('primary_color', '#000000')} onChange={(v) => onChange('primary_color', v)} />
        <ColorPickerInput label="Secondary Color" value={vs('secondary_color', '#666666')} onChange={(v) => onChange('secondary_color', v)} />
        <ColorPickerInput label="Background" value={vs('background_color', '#ffffff')} onChange={(v) => onChange('background_color', v)} />
        <ColorPickerInput label="Content Background" value={vs('content_background_color', '#ffffff')} onChange={(v) => onChange('content_background_color', v)} />
        <ColorPickerInput label="Table Header" value={vs('table_header_color', '#f8f9fa')} onChange={(v) => onChange('table_header_color', v)} />
        <ColorPickerInput label="Table Row 1" value={vs('table_row_color_1', '#ffffff')} onChange={(v) => onChange('table_row_color_1', v)} />
        <ColorPickerInput label="Table Row 2" value={vs('table_row_color_2', '#f8f9fa')} onChange={(v) => onChange('table_row_color_2', v)} />
      </SectionAccordion>

      {/* Section 4: Typography */}
      <SectionAccordion title="Typography">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Font Family</label>
          <select
            value={vs('font_family', 'Arial, Helvetica, sans-serif')}
            onChange={(e) => onChange('font_family', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f} value={f}>{f.split(',')[0]}</option>
            ))}
          </select>
        </div>
        <NumberInput label="Base Font Size" value={vn('base_font_size', 12)} min={8} max={16} suffix="pt" onChange={(v) => onChange('base_font_size', v)} />
        <NumberInput label="Header Font Size" value={vn('header_font_size', 18)} min={12} max={36} suffix="pt" onChange={(v) => onChange('header_font_size', v)} />
        <NumberInput label="Footer Font Size" value={vn('footer_font_size', 10)} min={6} max={14} suffix="pt" onChange={(v) => onChange('footer_font_size', v)} />
        <NumberInput label="Line Height" value={vn('line_height', 1.2)} min={1} max={2} step={0.1} suffix="x" onChange={(v) => onChange('line_height', v)} />
      </SectionAccordion>

      {/* Section 5: Layout Toggles */}
      <SectionAccordion title="Layout Toggles">
        <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Invoice Header</div>
        <Toggle label="Bill Number" checked={vb('show_bill_number')} onChange={(v) => onChange('show_bill_number', v)} />
        <Toggle label="Issue Date" checked={vb('show_issue_date')} onChange={(v) => onChange('show_issue_date', v)} />
        <Toggle label="Due Date" checked={vb('show_due_date')} onChange={(v) => onChange('show_due_date', v)} />
        <Toggle label="Billing Period" checked={vb('show_billing_period')} onChange={(v) => onChange('show_billing_period', v)} />

        <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-3 mb-1">Client & Matter</div>
        <Toggle label="Client Information" checked={vb('show_client_information')} onChange={(v) => onChange('show_client_information', v)} />
        <Toggle label="Matter Information" checked={vb('show_matter_information')} onChange={(v) => onChange('show_matter_information', v)} />

        <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-3 mb-1">Line Items</div>
        <Toggle label="Line Item Details" checked={vb('show_line_item_details')} onChange={(v) => onChange('show_line_item_details', v)} />
        <Toggle label="Activity Descriptions" checked={vb('show_activity_descriptions')} onChange={(v) => onChange('show_activity_descriptions', v)} />
        <Toggle label="Rates & Hours" checked={vb('show_rates_and_hours')} onChange={(v) => onChange('show_rates_and_hours', v)} />
        <Toggle label="UTBMS Codes" checked={vb('show_utbms_codes', false)} onChange={(v) => onChange('show_utbms_codes', v)} />
        <Toggle label="Group by Category" checked={vb('group_by_category', false)} onChange={(v) => onChange('group_by_category', v)} />
        <Toggle label="Group by Date" checked={vb('group_by_date', false)} onChange={(v) => onChange('group_by_date', v)} />

        <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-3 mb-1">Financial</div>
        <Toggle label="Subtotal" checked={vb('show_subtotal')} onChange={(v) => onChange('show_subtotal', v)} />
        <Toggle label="Tax Breakdown" checked={vb('show_tax_breakdown')} onChange={(v) => onChange('show_tax_breakdown', v)} />
        <Toggle label="Discounts" checked={vb('show_discounts')} onChange={(v) => onChange('show_discounts', v)} />
        <Toggle label="Total Amount" checked={vb('show_total_amount')} onChange={(v) => onChange('show_total_amount', v)} />
        <Toggle label="Amount Paid" checked={vb('show_amount_paid')} onChange={(v) => onChange('show_amount_paid', v)} />
        <Toggle label="Balance Due" checked={vb('show_balance_due')} onChange={(v) => onChange('show_balance_due', v)} />

        <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-3 mb-1">Payment</div>
        <Toggle label="Payment Instructions" checked={vb('show_payment_instructions')} onChange={(v) => onChange('show_payment_instructions', v)} />
      </SectionAccordion>

      {/* Section 6: Table Styling */}
      <SectionAccordion title="Table Styling">
        <Toggle label="Show Table Borders" checked={vb('show_table_borders')} onChange={(v) => onChange('show_table_borders', v)} />
        <Toggle label="Alternate Row Colors" checked={vb('alternate_row_colors')} onChange={(v) => onChange('alternate_row_colors', v)} />
      </SectionAccordion>

      {/* Section 7: Page Setup */}
      <SectionAccordion title="Page Setup">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Page Size</label>
          <select
            value={vs('page_size', 'A4')}
            onChange={(e) => onChange('page_size', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
          >
            <option value="A4">A4</option>
            <option value="Letter">US Letter</option>
            <option value="Legal">US Legal</option>
          </select>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300">Orientation</label>
          {(['portrait', 'landscape'] as const).map((o) => (
            <label key={o} className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="page_orientation"
                checked={vs('page_orientation', 'portrait') === o}
                onChange={() => onChange('page_orientation', o)}
                className="text-lex-accent"
              />
              <span className="text-gray-600 dark:text-gray-400 capitalize">{o}</span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label="Top" value={vn('margin_top', 10)} min={0} max={50} suffix="mm" onChange={(v) => onChange('margin_top', v)} />
          <NumberInput label="Bottom" value={vn('margin_bottom', 10)} min={0} max={50} suffix="mm" onChange={(v) => onChange('margin_bottom', v)} />
          <NumberInput label="Left" value={vn('margin_left', 15)} min={0} max={50} suffix="mm" onChange={(v) => onChange('margin_left', v)} />
          <NumberInput label="Right" value={vn('margin_right', 15)} min={0} max={50} suffix="mm" onChange={(v) => onChange('margin_right', v)} />
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <Toggle label="Watermark Draft Bills" checked={vb('watermark_draft_bills', false)} onChange={(v) => onChange('watermark_draft_bills', v)} />
          {vb('watermark_draft_bills', false) && (
            <TextInput label="Watermark Text" value={vs('watermark_text', 'DRAFT')} placeholder="DRAFT" onChange={(v) => onChange('watermark_text', v)} />
          )}
        </div>
      </SectionAccordion>

      {/* Section 8: Rich Content */}
      <SectionAccordion title="Rich Content">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Rich text editors for header, footer, payment instructions, and disclaimer will be available in a future update.
          For now, you can set the firm registration text below.
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Firm Registration Text</label>
          <textarea
            rows={2}
            value={vs('firm_registration_text')}
            onChange={(e) => onChange('firm_registration_text', e.target.value)}
            placeholder="Registered in England & Wales No. 12345678"
            className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
          />
        </div>
        <Toggle label="Show Firm Registration" checked={vb('include_firm_registration', false)} onChange={(v) => onChange('include_firm_registration', v)} />
        <Toggle label="Show Disclaimer" checked={vb('include_disclaimer', false)} onChange={(v) => onChange('include_disclaimer', v)} />
      </SectionAccordion>

      {/* Section 9: Advanced */}
      <SectionAccordion title="Advanced">
        <TextInput label="Language Code" value={vs('language_code', 'en')} placeholder="en" onChange={(v) => onChange('language_code', v)} />
        <TextInput label="Currency" value={vs('default_currency', 'USD')} placeholder="USD" onChange={(v) => onChange('default_currency', v)} />
        <TextInput label="Number Format Locale" value={vs('number_format_locale', 'en-US')} placeholder="en-US" onChange={(v) => onChange('number_format_locale', v)} />
        <Toggle label="Enable PDF Export" checked={vb('enable_pdf_export')} onChange={(v) => onChange('enable_pdf_export', v)} />
        <Toggle label="Enable HTML Export" checked={vb('enable_html_export', false)} onChange={(v) => onChange('enable_html_export', v)} />
      </SectionAccordion>
    </div>
  );
};

export default ThemeEditorForm;
